import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge class names so a caller's override actually wins over a component default. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Quality and coherence are 0-1 scores (DESIGN §9.4, §10.1); two decimals is all they mean. */
export function formatScore(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : n.toFixed(2);
}

/** 1284 -> "1,284". Counts in this bank are thousands, not millions; no compaction. */
export function formatCount(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : n.toLocaleString();
}

/**
 * Why a cluster member is grouped with its canonical (DESIGN §10.1) — or, when there
 * is no direct verdict, that it joined through a transitive chain (A≈B, B≈C, no
 * decision on A-C). Shared by Clusters and the question detail's cluster section so
 * there is one rendering of this line, not two that can drift.
 */
/**
 * The words for a run's `documentPhase` (queries.ts) — factored out so the runs
 * table and a single run's detail page can never drift to different vocabulary for
 * the same three states.
 */
export function runPhaseLabel(phase: "automated" | "review" | "settled"): {
  label: string;
  tone: "pipe" | "attention" | "good";
} {
  if (phase === "automated") return { label: "Running", tone: "pipe" };
  if (phase === "review") return { label: "Active · human review", tone: "attention" };
  return { label: "Settled", tone: "good" };
}

export function decisionSummary(
  decision: {
    cosine: number | null;
    entity_jaccard: number | null;
    decided_by: string | null;
    decided_at_layer: number | null;
    reasoning: string | null;
  } | null,
): string {
  if (!decision) return "no direct decision — joined through a chain";
  const reasoning = decision.reasoning ? ` — ${decision.reasoning.slice(0, 160)}` : "";
  return (
    `cosine ${formatScore(decision.cosine)} · entities ${formatScore(decision.entity_jaccard)} · ` +
    `${decision.decided_by ?? "?"} (L${decision.decided_at_layer ?? "?"})${reasoning}`
  );
}
